import { useState } from 'react';
import { ScrollText } from 'lucide-react';
import type { LedgerAccount, LedgerEntry, LedgerKind } from '@shared/ledger';

import { Button } from '@/components/ui/Button';
import { SelectField } from '@/components/ui/Field';
import { EmptyState, InlineError } from '@/components/ui/EmptyState';
import { Skeleton } from '@/components/ui/Skeleton';
import { useLedger } from '@/lib/queries';
import { toMessage } from '@/lib/api';
import { absoluteDateTime, relativeTime, signedUsdt, usdt } from '@/lib/format';
import {
  LEDGER_ACCOUNT_LABEL,
  LEDGER_ACCOUNT_ORDER,
  LEDGER_KIND_HINT,
  LEDGER_KIND_LABEL,
  LEDGER_KIND_ORDER,
} from '@/lib/moneyLabels';
import { NOUN, plural } from '@/lib/plural';

/**
 * The trader's own statement.
 *
 * This is what a trader opens when a number surprised them, so every row is
 * written as an explanation rather than as a database record: what happened, in
 * their words; how much moved and in which direction; which account it left the
 * balance of; what that account was left holding; and which payment caused it.
 *
 * The enum values themselves are never rendered. A row reading `dispute_loss`
 * answers nothing, and the trader who cannot read it will ask support instead.
 */

const PAGE_SIZE = 25;

/** `''` is the "no filter" option value — the empty string a `<select>` gives. */
const ANY = '';

export function LedgerTable() {
  const [account, setAccount] = useState<LedgerAccount | ''>(ANY);
  const [kind, setKind] = useState<LedgerKind | ''>(ANY);
  const [limit, setLimit] = useState(PAGE_SIZE);

  const isFiltered = account !== ANY || kind !== ANY;

  const ledger = useLedger({
    account: account === ANY ? null : account,
    kind: kind === ANY ? null : kind,
    limit,
  });

  // A narrower filter with a wide window loaded is not wrong, but it is slow
  // and confusing; every filter change starts the window again.
  const changeAccount = (next: LedgerAccount | ''): void => {
    setAccount(next);
    setLimit(PAGE_SIZE);
  };

  const changeKind = (next: LedgerKind | ''): void => {
    setKind(next);
    setLimit(PAGE_SIZE);
  };

  return (
    <div>
      <div className="grid gap-3 border-b border-line px-4 py-3 sm:grid-cols-2">
        <SelectField
          label="Счёт"
          value={account}
          onChange={(event) => changeAccount(event.target.value as LedgerAccount | '')}
        >
          <option value={ANY}>Все счета</option>
          {LEDGER_ACCOUNT_ORDER.map((value) => (
            <option key={value} value={value}>
              {LEDGER_ACCOUNT_LABEL[value]}
            </option>
          ))}
        </SelectField>

        <SelectField
          label="Операция"
          value={kind}
          onChange={(event) => changeKind(event.target.value as LedgerKind | '')}
        >
          <option value={ANY}>Все операции</option>
          {LEDGER_KIND_ORDER.map((value) => (
            <option key={value} value={value}>
              {LEDGER_KIND_LABEL[value]}
            </option>
          ))}
        </SelectField>
      </div>

      <LedgerBody
        isPending={ledger.isPending}
        isFiltered={isFiltered}
        error={ledger.isError ? toMessage(ledger.error, 'Не удалось загрузить выписку.') : null}
        onRetry={() => void ledger.refetch()}
        entries={ledger.data?.items ?? []}
        total={ledger.data?.total ?? 0}
        isFetching={ledger.isFetching}
        onShowMore={() => setLimit((current) => current + PAGE_SIZE)}
        onReset={() => {
          setAccount(ANY);
          setKind(ANY);
          setLimit(PAGE_SIZE);
        }}
      />
    </div>
  );
}

interface LedgerBodyProps {
  isPending: boolean;
  isFiltered: boolean;
  error: string | null;
  onRetry: () => void;
  entries: LedgerEntry[];
  total: number;
  isFetching: boolean;
  onShowMore: () => void;
  onReset: () => void;
}

function LedgerBody({
  isPending,
  isFiltered,
  error,
  onRetry,
  entries,
  total,
  isFetching,
  onShowMore,
  onReset,
}: LedgerBodyProps) {
  if (isPending) {
    return (
      <div className="divide-y divide-line" role="status" aria-busy="true">
        <span className="sr-only">Загружаю выписку</span>
        {Array.from({ length: 5 }, (_, index) => (
          <div key={index} className="px-4 py-3.5">
            <Skeleton className="h-4 w-40" />
            <Skeleton className="mt-2 h-3 w-full max-w-prose" />
            <Skeleton className="mt-2 h-2.5 w-32" />
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <InlineError message={error} onRetry={onRetry} />
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <EmptyState
        icon={ScrollText}
        title={isFiltered ? 'По этому фильтру записей нет' : 'Записей пока нет'}
        description={
          isFiltered
            ? 'Попробуйте выбрать другой счёт или тип операции.'
            : 'Здесь будет каждое движение по вашим счетам: заморозка под платёж, комиссия, вывод. Первая запись появится вместе с первым платежом.'
        }
        action={
          isFiltered ? (
            <Button onClick={onReset}>Сбросить фильтры</Button>
          ) : undefined
        }
      />
    );
  }

  const hasMore = entries.length < total;

  return (
    <>
      <ul className="divide-y divide-line">
        {entries.map((entry) => (
          <LedgerRow key={entry.id} entry={entry} />
        ))}
      </ul>

      {hasMore ? (
        <div className="border-t border-line px-4 py-3">
          <Button fullWidth onClick={onShowMore} isLoading={isFetching} className="sm:w-auto">
            Показать ещё
          </Button>
        </div>
      ) : null}

      <p className="border-t border-line px-4 py-2.5 text-xs text-fg-muted">
        Показано <span className="num">{entries.length}</span> из{' '}
        <span className="num">{total}</span> {plural(total, NOUN.entry)}.
      </p>
    </>
  );
}

function LedgerRow({ entry }: { entry: LedgerEntry }) {
  const isCredit = entry.amountUsdt >= 0;

  return (
    <li className="px-4 py-3.5">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <p className="min-w-0 text-base font-medium text-fg">{LEDGER_KIND_LABEL[entry.kind]}</p>
        {/* Never coloured. The sign is the signal, and it is spelled out for a
            screen reader because a lone U+2212 is not reliably announced. */}
        <p className="num shrink-0 whitespace-nowrap text-md font-medium text-fg">
          <span className="sr-only">{isCredit ? 'зачисление ' : 'списание '}</span>
          {signedUsdt(entry.amountUsdt)}
        </p>
      </div>

      <p className="mt-1 max-w-prose text-sm leading-normal text-fg-secondary">
        {LEDGER_KIND_HINT[entry.kind]}
      </p>

      {entry.reason ? (
        <p className="mt-1 max-w-prose text-sm text-fg-secondary">Причина: {entry.reason}</p>
      ) : null}

      <div className="mt-2 flex flex-wrap items-baseline gap-x-3 gap-y-0.5 text-2xs text-fg-muted">
        <span>{LEDGER_ACCOUNT_LABEL[entry.account]}</span>
        <span className="num">стало {usdt(entry.balanceAfter)}</span>
        {entry.transactionId ? (
          <span className="num" title={entry.transactionId}>
            платёж {entry.transactionId.slice(0, 8)}
          </span>
        ) : null}
        <time dateTime={entry.createdAt} title={absoluteDateTime(entry.createdAt)}>
          {relativeTime(entry.createdAt)}
        </time>
      </div>
    </li>
  );
}
